/**
 * @lodex-pro/web-portal/hooks/useReportSchedules — the operator admin's scheduled emailed reports
 * (Operator Reports (b), migration 0257). OPERATOR_ADMIN only on the API
 * (MANAGE_OPERATOR_REPORT_SCHEDULES); the Reports page never mounts these hooks for anyone else.
 *
 *   GET    /v1/operator-portal/reports/schedules                the schedules, plan and limits
 *   POST   /v1/operator-portal/reports/schedules                add
 *   PATCH  /v1/operator-portal/reports/schedules/:id            change; saving makes you the owner
 *   DELETE /v1/operator-portal/reports/schedules/:id            remove, with its history
 *   POST   /v1/operator-portal/reports/schedules/:id/send-now   last period, to the caller only
 *
 * Every write invalidates the list. Response types are `import type` only (DEC-149 §9).
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type {
  CreateOperatorReportScheduleRequest,
  OperatorReportScheduleView,
  OperatorReportSchedulesResponse,
  UpdateOperatorReportScheduleRequest,
} from '@lodex-pro/shared/validation/operator-report-schedules';

export const REPORT_SCHEDULES_KEY: readonly string[] = ['operator-portal', 'report-schedules'];
const BASE = '/v1/operator-portal/reports/schedules';

/** POST /:id/send-now (services/operator-report-sender.ts ReportPreviewResult). */
export interface ReportPreviewResult {
  readonly status: 'SENT' | 'NO_TICKETS';
  readonly to: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly ticketCount: number;
}

export function useReportSchedules(): ReturnType<
  typeof useApiQuery<OperatorReportSchedulesResponse>
> {
  const opts = useApiOpts();
  return useApiQuery<OperatorReportSchedulesResponse>(opts, BASE, {
    queryKey: REPORT_SCHEDULES_KEY,
    staleTime: 10_000,
  });
}

function useInvalidate(): () => void {
  const qc = useQueryClient();
  return (): void => {
    void qc.invalidateQueries({ queryKey: REPORT_SCHEDULES_KEY });
  };
}

export function useAddReportSchedule(): ReturnType<
  typeof useApiMutation<
    CreateOperatorReportScheduleRequest,
    { schedule: OperatorReportScheduleView }
  >
> {
  const opts = useApiOpts();
  const invalidate = useInvalidate();
  return useApiMutation<
    CreateOperatorReportScheduleRequest,
    { schedule: OperatorReportScheduleView }
  >(opts, BASE, 'POST', { onSettled: invalidate });
}

export function useUpdateReportSchedule(
  id: string,
): ReturnType<
  typeof useApiMutation<
    UpdateOperatorReportScheduleRequest,
    { schedule: OperatorReportScheduleView }
  >
> {
  const opts = useApiOpts();
  const invalidate = useInvalidate();
  return useApiMutation<
    UpdateOperatorReportScheduleRequest,
    { schedule: OperatorReportScheduleView }
  >(opts, `${BASE}/${id}`, 'PATCH', { onSettled: invalidate });
}

export function useRemoveReportSchedule(
  id: string,
): ReturnType<typeof useApiMutation<Record<string, never>, null>> {
  const opts = useApiOpts();
  const invalidate = useInvalidate();
  return useApiMutation<Record<string, never>, null>(opts, `${BASE}/${id}`, 'DELETE', {
    onSettled: invalidate,
  });
}

export function useSendReportNow(
  id: string,
): ReturnType<typeof useApiMutation<Record<string, never>, ReportPreviewResult>> {
  const opts = useApiOpts();
  return useApiMutation<Record<string, never>, ReportPreviewResult>(
    opts,
    `${BASE}/${id}/send-now`,
    'POST',
  );
}
